import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { selectors } from '../reducer'
import { selectors as profileSelectors } from '../../profile/reducer'
import * as profileActions from '../../profile/actions'
import * as apiActions from 'api-actions'
import * as globalActions from '../../actions'
import { flashErrorMessage, flashSuccessMessage } from 'redux-flash'
import { Path, CREDIT_CARD_OPTION } from 'config/portal'
import {
  TOAST_ERROR_CONTACT_SUPPORT,
  TOAST_ERROR_CREDIT_CARD,
} from 'config/notifications'
import { KNOWN_PAYMENT_ERRORS } from 'config/creditCardErrors'
import {
  formatFileName,
  useParticipantsSummaryNotificationServices,
  downloadDiscardedParticipants,
  displayInteger,
  formatCurrency,
  removeAttributes,
} from 'utils'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import { isEmpty, map } from 'lodash'
import { Icon as DownloadIcon } from 'images/download.svg'
import { ConfirmModal, FullPageSpinner, ComponentFailure } from 'components'
import { selectors as globalSelectors } from 'global-reducer'
import { selectors as apiSelectors } from 'lp-redux-api'
import { RecordTypes } from 'flatfile-config'
import { US_COUNTRY_VALUE } from 'config/locations'
import { ReviewAndSubmitForm } from '../forms'

const propTypes = {
  currentUser: Types.user.isRequired,
  participantsAgreementHash: PropTypes.object,
  participantFileName: PropTypes.string,
  createNotificationServicesParticipantsData: PropTypes.func.isRequired,
  deletePaymentMethod: PropTypes.func.isRequired,
  flashErrorMessageHandler: PropTypes.func.isRequired,
  participantsForSubmission: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  discardedParticipants: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  isSubmittingParticipantsData: PropTypes.bool.isRequired,
  paymentMethod: PropTypes.array,
  fetchPaymentMethod: PropTypes.func.isRequired,
  shouldTriggerPaymentInfoFetch: PropTypes.bool.isRequired,
  setShouldTriggerPaymentInfoFetch: PropTypes.func.isRequired,
  saveCompletePaymentDetails: PropTypes.func.isRequired,
  notificationsFeesCost: PropTypes.object,
  generateNotificationsFeesCost: PropTypes.func.isRequired,
  flashSuccessMessageHandler: PropTypes.func.isRequired,
}

const defaultProps = {
  participantsAgreementHash: null,
  participantFileName: null,
  notificationsFeesCost: null,
}

const FILE_NAME_PREFIX = 'RSP_'
const FILE_NAME_MANUAL_ENTRY = 'RSP_Inspira_Financial_Manual_Entry.xlsx'

const scrubData = (val) => val.replaceAll(/[$]/g, '')

function ReviewAndSubmitView({
  currentUser,
  participantsAgreementHash,
  participantFileName,
  createNotificationServicesParticipantsData,
  flashErrorMessageHandler,
  participantsForSubmission,
  discardedParticipants,
  isSubmittingParticipantsData,
  paymentMethod,
  fetchPaymentMethod,
  deletePaymentMethod,
  shouldTriggerPaymentInfoFetch,
  setShouldTriggerPaymentInfoFetch,
  saveCompletePaymentDetails,
  generateNotificationsFeesCost,
  notificationsFeesCost,
  flashSuccessMessageHandler,
}) {
  const history = useHistory()
  const { state } = useLocation()
  const recordFiles = state?.recordFiles || {}
  const [error, setError] = useState(false)
  const [isNewPayment, setIsNewPayment] = useState(false)
  const [showReplacePayment, setShowReplacePayment] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasDownloaded, setHasDownloaded] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const {
    data: participantsSummaryData,
    overview: participantsSummaryOverview,
  } = useParticipantsSummaryNotificationServices(participantsForSubmission)

  useEffect(() => {
    async function getPaymentMethod() {
      try {
        setIsLoading(true)
        await fetchPaymentMethod()
        setIsLoading(false)
      } catch {
        setError(true)
      }
    }
    if (!paymentMethod || shouldTriggerPaymentInfoFetch) {
      getPaymentMethod()
      setShouldTriggerPaymentInfoFetch(false)
    }
  }, [paymentMethod, shouldTriggerPaymentInfoFetch])

  const hasPaymentMethod = !isEmpty(paymentMethod)
  const hasCardOnFile =
    paymentMethod &&
    !Array.isArray(paymentMethod) &&
    typeof paymentMethod === 'object'

  useEffect(() => {
    async function generateTotalFeesCost() {
      try {
        await generateNotificationsFeesCost({
          participantsCount: participantsForSubmission.length,
        })
      } catch (e) {
        setError(true)
      }
    }
    generateTotalFeesCost()
  }, [participantsForSubmission])

  const totalPaymentDue = notificationsFeesCost?.summary.totalCost
  const handleDeletePayment = async () => {
    try {
      await deletePaymentMethod()
      setShowReplacePayment(true)
    } catch (e) {
      flashErrorMessage(TOAST_ERROR_CONTACT_SUPPORT)
    }
  }

  const handleSubmit = async (values) => {
    if (!values.hasConfirmed) {
      if (!isEmpty(discardedParticipants) && !hasDownloaded) {
        setShowModal(true)
        return
      }
    }
    // scrub the '$' currency from IRA amounts before submission
    const scrubbedParticipantsForSubmission = map(
      participantsForSubmission,
      ({ totalBalance, vestedBalance, ...rest }) => ({
        ...rest,
        totalBalance: scrubData(totalBalance),
        vestedBalance: scrubData(vestedBalance),
      })
    )
    const formData = new FormData()
    const cleanedParticipants = removeAttributes(
      scrubbedParticipantsForSubmission,
      ['reasonForDiscard', 'mappedPlanName']
    )
    const participantData = {
      fileName: participantFileName
        ? formatFileName(participantFileName, FILE_NAME_PREFIX)
        : FILE_NAME_MANUAL_ENTRY,
      participants: cleanedParticipants,
      summary: {
        totalPlans: participantsSummaryOverview.totalPlans,
        totalParticipants: participantsSummaryOverview.totalParticipants,
        totalCost: formatCurrency(notificationsFeesCost?.summary.totalCost),
      },
      contactID: currentUser.contactID,
      ...(values.promoApplied &&
        values.promoCode && {
          promoCode: values.promoCode,
        }),
    }
    formData.append('participantData', JSON.stringify(participantData))
    for (const [name, file] of Object.entries(recordFiles)) {
      if (file instanceof File) {
        formData.append(name, file)
      }
    }
    const paymentDetails = isNewPayment
      ? {
          firstName: values.paymentDetails.firstName,
          lastName: values.paymentDetails.lastName,
          country: values.paymentDetails.country,
          state: values.paymentDetails.state,
          city: values.paymentDetails.city,
          address1: values.paymentDetails.address1,
          address2: values.paymentDetails.address2,
          zipCode: values.paymentDetails.zip,
          paymentToken: values.paymentDetails.cardData.paymentToken,
          fraudId: values.paymentDetails.cardData.fraud,
          creditCardLastFour: values.paymentDetails.cardData.last4Digits,
          cardExpMonth: values.paymentDetails.cardData.exp.split('/')[0],
          cardExpYear: values.paymentDetails.cardData.exp.split('/')[1],
          cardType: values.paymentDetails.cardData.ccType,
          cardStatus: 'Active',
          cardLabel: values.paymentDetails.cardData.cardLabel,
          phone: null,
          email: null,
        }
      : null
    try {
      if (isNewPayment) {
        const paymentResponse = await saveCompletePaymentDetails({
          paymentDetails,
        })
        // Error handling for IsSuccess: false from /saveCompletePaymentDetails
        if (paymentResponse.IsSuccess === false) {
          const paymentError = paymentResponse?.Errors?.[0]

          const knownError = KNOWN_PAYMENT_ERRORS.find(
            (error) => error.name === paymentError.Name
          )
          if (knownError) {
            flashErrorMessageHandler(knownError.description)
          } else {
            flashErrorMessageHandler(TOAST_ERROR_CREDIT_CARD)
          }
          return
        }
      }

      const nsResponse = await createNotificationServicesParticipantsData({
        formData,
      })
      // Error handling for IsSuccess: false from /notificationParticipantFile
      if (nsResponse?.IsSuccess === false) {
        const paymentError = nsResponse?.Errors?.[0]
        const knownError = KNOWN_PAYMENT_ERRORS.find(
          (error) => error.name === paymentError.Name
        )
        if (knownError) {
          flashErrorMessageHandler(knownError.description)
        } else {
          flashErrorMessageHandler(TOAST_ERROR_CREDIT_CARD)
        }
        return
      }

      setShouldTriggerPaymentInfoFetch(true)

      history.push({
        pathname: Path.NOTIFICATIONS_HISTORY,
        state: {
          ignorePrompt: true,
          showNotificationsOrderSuccessModal: true,
        },
      })
    } catch (e) {
      flashErrorMessageHandler(TOAST_ERROR_CREDIT_CARD)
    }
  }

  const handleDownload = () => {
    setHasDownloaded(true)
    downloadDiscardedParticipants({
      participants: discardedParticipants,
      originalFileName: participantFileName,
      recordType: RecordTypes.ARO_PARTICIPANT,
    })
  }

  if (isEmpty(participantsAgreementHash)) {
    return <Redirect to="/notification-services/upload-file" />
  }

  if (error) return <ComponentFailure />

  return (
    <div className="card full-height upload-participant-file-view-container">
      {isSubmittingParticipantsData && <FullPageSpinner showBackground />}
      <div className="form-masthead">
        <h2>Lastly, review your submission and accept the disclosures</h2>
      </div>
      <div className="form-header">
        <h3 className="flow-section-headers">Submission details</h3>
      </div>
      {!isEmpty(discardedParticipants) && (
        <div className="form-section discarded-participants">
          <div className="form-header">
            <h3 className="is-marginless">
              Participants not included in submission
            </h3>
          </div>
          <p>
            Some participants won’t be included in your submission if required
            information fails validation or isn’t provided. Download the data
            for these participants for your records and resubmit them once you
            have the required information.
          </p>
          <div className="download-container">
            <span>
              Total discarded participants:{' '}
              {displayInteger(discardedParticipants.length)}
            </span>
            <button type="button" onClick={handleDownload}>
              <DownloadIcon aria-hidden="true" />
              Download participant data
            </button>
          </div>
        </div>
      )}
      <section className="confirm-order-details">
        <dl>
          <div className="user-details">
            <dt>Total participants</dt>
            <dd>{participantsForSubmission?.length}</dd>
          </div>
        </dl>
      </section>
      <ReviewAndSubmitForm
        initialValues={{
          paymentDetails: {
            paymentType: CREDIT_CARD_OPTION.value,
            firstName: '',
            lastName: '',
            cardData: '',
            country: US_COUNTRY_VALUE,
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
          },
          confirmPayment: false,
          promoCode: '',
          promoApplied: false,
          discountedTotal: null,
        }}
        hasPaymentMethod={hasPaymentMethod}
        participantsSummaryData={participantsSummaryData}
        onSubmit={(params) => handleSubmit(params)}
        paymentMethod={paymentMethod}
        onConfirmDeletePayment={handleDeletePayment}
        isNewPayment={isNewPayment}
        setIsNewPayment={setIsNewPayment}
        isLoading={isLoading}
        showReplacePayment={showReplacePayment}
        totalPaymentDue={totalPaymentDue}
        hasCardOnFile={hasCardOnFile}
        flashSuccessMessageHandler={flashSuccessMessageHandler}
        applyPromoCode={generateNotificationsFeesCost}
        totalParticipants={participantsForSubmission.length}
      />
      {showModal && (
        <ConfirmModal
          onClose={() => setShowModal(false)}
          onConfirm={async () => {
            setShowModal(false)
            handleSubmit({ hasConfirmed: true })
          }}
          confirmContent="Yes, Submit"
        >
          <h2>Download discard participants</h2>
          <p>
            Are you sure you want to submit without downloading the discarded
            participants?
          </p>
        </ConfirmModal>
      )}
    </div>
  )
}

ReviewAndSubmitView.propTypes = propTypes
ReviewAndSubmitView.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    currentUser: globalSelectors.currentUser(state),
    participantFileName: selectors.participantFileName(state),
    participantsAgreementHash: selectors.participantsAgreementHash(state),
    participantsForSubmission: selectors.participantsForSubmission(state),
    discardedParticipants: selectors.discardedParticipants(state),
    isSubmittingParticipantsData: apiSelectors.isLoading(
      state,
      apiActions.createNotificationServicesParticipantsData
    ),
    paymentMethod: profileSelectors.paymentMethod(state),
    shouldTriggerPaymentInfoFetch:
      profileSelectors.shouldTriggerPaymentInfoFetch(state),
    notificationsFeesCost: selectors.notificationsFeesCost(state),
  }
}

const mapDispatchToProps = {
  showSuccessModal: globalActions.showSuccessModal,
  createNotificationServicesParticipantsData:
    apiActions.createNotificationServicesParticipantsData,
  flashErrorMessageHandler: flashErrorMessage,
  deletePaymentMethod: apiActions.deletePaymentMethod,
  fetchPaymentMethod: apiActions.fetchPaymentMethod,
  setShouldTriggerPaymentInfoFetch:
    profileActions.setShouldTriggerPaymentInfoFetch,
  saveCompletePaymentDetails: apiActions.saveCompletePaymentDetails,
  generateNotificationsFeesCost: apiActions.generateNotificationsFeesCost,
  flashSuccessMessageHandler: flashSuccessMessage,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  ReviewAndSubmitView
)
